import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import FileDrop from "@/components/site/FileDrop";

const inputCls =
  "w-full bg-[#F3EDE3] border border-[#DCD3C3] px-4 py-3.5 text-sm text-[#0A0A0A] placeholder-[#a79f90] focus:outline-none focus:border-[#B38728] transition-colors";

export default function ReturnForm() {
  const [form, setForm] = useState({ name: "", email: "", item_title: "", order_ref: "", reason: "Changed my mind", details: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/returns", fd);
      toast.success(`Return request received — your reference is ${data.ticket}. We'll reply within 24 hours.`);
      setForm({ name: "", email: "", item_title: "", order_ref: "", reason: "Changed my mind", details: "" });
      setFiles([]);
    } catch (err) {
      toast.error(formatApiError(err, "Could not send your return request"));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 border-t border-[#DCD3C3] pt-8" data-testid="return-form">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B38728] flex items-center gap-2">
        <RotateCcw className="w-3.5 h-3.5" /> Start a Return
      </p>
      <p className="text-sm text-[#5C564C] mt-3 leading-relaxed">
        Fill this in and your request lands with us instantly, with its own reference number — no back-and-forth needed.
      </p>
      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Name *</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="return-name-input" className={`mt-2 ${inputCls}`} placeholder="Your name" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Email *</label>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="return-email-input" className={`mt-2 ${inputCls}`} placeholder="The email you ordered with" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Item *</label>
          <input required value={form.item_title} onChange={(e) => setForm({ ...form, item_title: e.target.value })} data-testid="return-item-input" className={`mt-2 ${inputCls}`} placeholder="e.g. Classic Space Blue Astronaut" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Reason</label>
          <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="return-reason-select" className={`mt-2 ${inputCls}`}>
            <option>Changed my mind</option>
            <option>Damaged in transit</option>
            <option>Not as described</option>
            <option>Other</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Anything we should know?</label>
          <textarea rows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="return-details-input" className={`mt-2 ${inputCls}`} placeholder="Condition on arrival, what went wrong, photos help…" />
        </div>
        <div className="sm:col-span-2">
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C] block mb-2">Photos (optional, helps us move faster)</label>
          <FileDrop files={files} setFiles={setFiles} testId="return-file-drop" />
        </div>
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="return-submit-button"
        className="mt-6 bg-[#0A0A0A] text-[#F3EDE3] px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50 flex items-center gap-3"
      >
        {sending && <Loader2 className="w-4 h-4 animate-spin" />}
        {sending ? "Sending…" : "Submit Return Request"}
      </button>
    </form>
  );
}
